import React, { useCallback, useMemo } from "react";
import useWidgetFilterNavigator from "../../../../useWidgetFilterNavigator";
import { createColumnHelper } from "@tanstack/react-table";
import { GET_TOP_ACCOUNTS_DATA } from "../../../../WidgetDataApi";
import TopFeatureTable, {
  BaseTopFeatureRow,
  WidgetTableProps,
} from "../TopFeatureTable";
import useQueryWithRetries from "../../../../../Tools/useQueryWithRetries";
import {
  Tooltip,
  useIcon,
  TableCell,
  tableCellTypesEnum,
} from "@SeemplicityDev/shared";

interface TopCloudAccountRow extends BaseTopFeatureRow {
  cloud_account: string;
  provider: string;
}

const TopCloudAccountsTable: React.FC<WidgetTableProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  id,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const { data, error, loading, refetch } = useQueryWithRetries({
    query: GET_TOP_ACCOUNTS_DATA,

    variables: {
      widget_definition_key: "top_10_vulnerable_cloud_accounts",
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id ?? null,
  });

  const onChartClick = useCallback(
    (severity: string, { cloud_account, provider }: TopCloudAccountRow) => {
      onFilterClick(
        [["in", "severity", severity]],
        [
          ["in", "cloud_provider", provider],
          ["in", "cloud_account", cloud_account],
        ]
      );
    },
    []
  );

  const { getProviderIcon } = useIcon(isForExport ? "LIGHT" : undefined);

  const columnHelper = createColumnHelper<TopCloudAccountRow>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("cloud_account", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <div className="account-cell">
              <Tooltip content={props.row.original.provider}>
                <img
                  className={"account-icon"}
                  alt={props.row.original.provider}
                  src={
                    getProviderIcon(props.row.original.provider) || undefined
                  }
                />
              </Tooltip>
              <Tooltip content={props.getValue()}>
                <div className={"account-text"}>{props.getValue()}</div>
              </Tooltip>
            </div>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Cloud Account",
        minSize: 250,
      }),
    ];
  }, [getProviderIcon]);

  return (
    <TopFeatureTable
      data={data}
      columns={columns}
      onChartClick={onChartClick}
      loading={loading}
      error={error}
      refetch={refetch}
      id={id}
      isForExport={isForExport}
      setTotalWidgets={setTotalWidgets}
      index={index}
      identifier={"cloud_account"}
      setIsReadyToExport={setIsReadyToExport}
    />
  );
};

export default TopCloudAccountsTable;
